import { useCallback, useMemo } from "react";
import { Plus, UserCheck } from "lucide-react";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { EmployeeTable } from "@/components/table/EmployeeTable";
import { useAllEmployees, useUpdateEmployeeField } from "@/hooks/useEmployees";
import type { Employee } from "@/components/table/types";
import { Button } from "@/components/ui/button";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/table/EmptyState";
import { useListUrlState } from "@/hooks/useListUrlState";

const SCOPES = ["internal", "external", "all"] as const;
type EmployeeScope = (typeof SCOPES)[number];
// The select filters, kept in the URL; date ranges stay in the table.
const FILTER_KEYS = ["status", "gender", "labels", "qualificationLevel", "role"] as const;

export default function Employees() {
  const { data: employees, isLoading, error, refetch } = useAllEmployees();
  const updateEmployeeField = useUpdateEmployeeField();
  const navigate = useNavigate();
  const { i18n } = useLingui();

  // Search, scope and filters live in the URL, so Back from an employee
  // returns to the same filtered list.
  const list = useListUrlState<EmployeeScope, (typeof FILTER_KEYS)[number]>({
    scopes: SCOPES,
    defaultScope: "internal",
    filterKeys: FILTER_KEYS,
  });
  const scope = list.scope ?? "internal";

  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    if (scope === "all") return employees;
    if (scope === "external") return employees.filter((e) => e.external);
    return employees.filter((e) => !e.external);
  }, [employees, scope]);

  // The table seeds its filter chips from the URL once; `key={list.syncKey}`
  // re-mounts it when the URL changes from outside (sidebar, palette, Back).
  const initialFilters: ColumnFiltersState = FILTER_KEYS.flatMap((key) =>
    list.filters[key] ? [{ id: key, value: list.filters[key] }] : []
  );

  const { setFilters } = list;
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    const next: Partial<Record<(typeof FILTER_KEYS)[number], string[]>> = {};
    for (const key of FILTER_KEYS) {
      const value = filters.find((f) => f.id === key)?.value;
      if (Array.isArray(value) && value.length > 0) next[key] = value as string[];
    }
    setFilters(next);
  }, [setFilters]);

  const handleCellUpdate = async (
    rowId: string,
    field: keyof Employee,
    newValue: Employee[keyof Employee]
  ): Promise<void> => {
    await updateEmployeeField.mutateAsync({
      id: rowId,
      field,
      value: newValue,
    });
  };

  const addEmployee = useCallback(() => navigate("/employees/create"), [navigate]);
  const addEmployeeLabel = i18n._({ id: "employees.list.add", message: "Add employee" });
  const hasAnyEmployee = (employees?.length ?? 0) > 0;

  const emptyState = (
    <EmptyState
      icon={<UserCheck className="h-10 w-10" />}
      title={
        hasAnyEmployee
          ? i18n._({ id: "employees.list.emptyScope.title", message: "No employees in this view" })
          : i18n._({ id: "employees.list.empty.title", message: "No employees yet" })
      }
      action={
        <div className="flex flex-wrap justify-center gap-2">
          {hasAnyEmployee && (
            <Button size="sm" variant="outline" onClick={() => list.setScope("all")}>
              {i18n._({ id: "employees.list.showAll", message: "Show all employees" })}
            </Button>
          )}
          <Button size="sm" onClick={addEmployee}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addEmployeeLabel}
          </Button>
        </div>
      }
    />
  );

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        title={i18n._({ id: "nav.employees", message: "Employees" })}
        actions={
          <Button onClick={addEmployee}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addEmployeeLabel}
          </Button>
        }
      />

      <div className="min-h-0 flex-1">
        {error && !isLoading ? (
          <QueryErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <EmployeeTable
            key={list.syncKey}
            data={filteredEmployees}
            isLoading={isLoading}
            onCellUpdate={handleCellUpdate}
            enableSelection={true}
            initialFilters={initialFilters}
            onFilterChange={handleFilterChange}
            searchTerm={list.search}
            onSearchChange={list.setSearch}
            emptyState={emptyState}
            toolbarRight={
              <SegmentedToggle<EmployeeScope>
                className="ml-auto"
                value={scope}
                onValueChange={list.setScope}
                aria-label={i18n._({ id: "employees.filter.scopeLabel", message: "Which employees" })}
                options={[
                  { value: "internal", label: i18n._({ id: "employees.filter.internal", message: "Internal" }) },
                  { value: "external", label: i18n._({ id: "employees.filter.external", message: "External" }) },
                  { value: "all", label: i18n._({ id: "employees.filter.all", message: "All" }) },
                ]}
              />
            }
          />
        )}
      </div>
    </PageContainer>
  );
}
